import { STATED_CURRENCIES, formatMinorUnits } from "./money";

/**
 * WHAT REVIEW STATES ABOUT PAYING (booking-v1 1.14.0's and 1.19.0's facts,
 * read before the client commits; the payments flip branch). Called only with
 * the payments gate open: the caller tests the gate first, so a closed build
 * folds this module away.
 *
 * - null: not an app-paid professional (IN_PERSON_ONLY, or the collection
 *   mode unreadable: "show no payment claim then"). Review keeps its
 *   in-person card.
 * - `kind`, "deposit" / "full": what the professional collects in the app.
 * - `when` (G1, the Dev window, 2026-10-29), from the professional's
 *   `bookingConfirmationMode` (1.19.0):
 *   - "now": INSTANT. The booking leaves for Stripe from this press.
 *   - "afterApproval": REQUEST. Nothing is paid now: the business accepts
 *     first, and the payment is asked for then. Never "now", never Stripe.
 *   - "unknown": any other value (OPEN in the contract) or none. Neutral
 *     words, "never a confirm promise or a request promise".
 * - `amount`: the server's amount for this service
 *   (`PublicService.appCollectableInCents`, computed by the same function the
 *   booking commits), rendered around the approved words, never computed
 *   here; null when Book may not state it (null, not positive, or a currency
 *   Book does not state). Review then claims no amount; the booking answer
 *   carries the server's own arithmetic. The in-person remainder is not on
 *   Review at all until Backend serves it (ruling (b), 2026-10-29).
 *
 * v1.1 charges in USD only (Backend, 2026-10-17): the profile names no
 * currency, so the amount is stated in USD.
 */
export interface ReviewPayNow {
  readonly kind: "deposit" | "full";
  readonly when: "now" | "afterApproval" | "unknown";
  readonly amount: string | null;
}

const PROFILE_CURRENCY = "USD";

export function reviewPayNow(
  collectionMode: string | null | undefined,
  appCollectableInCents: number | null | undefined,
  confirmationMode: string | null | undefined,
  locale: string
): ReviewPayNow | null {
  const kind = collectionMode === "DEPOSIT_REQUIRED" ? "deposit" : collectionMode === "FULL_PREPAY_REQUIRED" ? "full" : null;
  if (kind === null) return null;
  const when = confirmationMode === "INSTANT" ? "now" : confirmationMode === "REQUEST" ? "afterApproval" : "unknown";
  const statable =
    typeof appCollectableInCents === "number" && Number.isSafeInteger(appCollectableInCents) && appCollectableInCents > 0 && STATED_CURRENCIES.has(PROFILE_CURRENCY);
  return { kind, when, amount: statable ? formatMinorUnits(appCollectableInCents, PROFILE_CURRENCY, locale) : null };
}
